<template>
  <div class="main-wrapper">
    <layout-header></layout-header>
    <layout-sidebar></layout-sidebar>
    <div class="page-wrapper">
      <div class="content container-fluid">

        <div class="page-header">
          <div class="row">
            <div class="col">
              <h3 class="page-title" v-if="customerName">Welcome, {{ customerName }}!</h3>
              <h3 class="page-title" v-else>Welcome!</h3>
              <p class="text-muted">Here's your account overview</p>
            </div>
            <div class="col-auto text-right">
              <button @click="redirectToPayment" class="btn btn-primary">
                <i class="fas fa-credit-card"></i> Make a Payment
              </button>
            </div>
          </div>
        </div>

        <!-- Customer Metrics -->
        <div class="row vld-parent">
          <div class="col-xl-3 col-sm-6 col-12">
            <div class="card">
              <div class="card-body">
                <div class="dash-widget-header">
									<span class="dash-widget-icon bg-success" style="height: 60px; width: 60px; font-size: 30px">
										<i class="fas fa-wallet"></i>
									</span>
                  <div class="dash-widget-info">
                    <h3>{{ displayAmount(currency, accountBalance) }}</h3>
                    <h6 class="text-muted">You Owe</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-xl-3 col-sm-6 col-12">
            <div class="card">
              <div class="card-body">
                <div class="dash-widget-header">
									<span class="dash-widget-icon bg-success" style="height: 60px; width: 60px; font-size: 30px">
										<i class="fas fa-money-bill-wave"></i>
									</span>
                  <div class="dash-widget-info">
                    <h3>{{ displayAmount(currency, totalPayments) }}</h3>
                    <h6 class="text-muted">Total Payments</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-xl-3 col-sm-6 col-12">
            <div class="card">
              <div class="card-body">
                <div class="dash-widget-header">
									<span class="dash-widget-icon bg-warning" style="height: 60px; width: 60px; font-size: 30px">
										<i class="fas fa-file-invoice"></i>
									</span>
                  <div class="dash-widget-info">
                    <h3>{{ displayAmount(currency, totalInvoices) }}</h3>
                    <h6 class="text-muted">Total Invoices</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-xl-3 col-sm-6 col-12">
            <div class="card">
              <div class="card-body">
                <div class="dash-widget-header">
									<span class="dash-widget-icon bg-info" style="height: 60px; width: 60px; font-size: 30px">
										<i class="fas fa-exchange-alt"></i>
									</span>
                  <div class="dash-widget-info">
                    <h3>{{ recentTransactionsCount }}</h3>
                    <h6 class="text-muted">Transactions</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Transactions -->
        <div class="row">
          <div class="col-md-12">
            <div class="card">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="card-title mb-0">Recent Transactions</h5>
                <button @click="viewStatement" class="btn btn-sm btn-outline-primary">
                  View Full Statement
                </button>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Type</th>
                        <th class="text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!recentTransactions.length && !isLoading">
                        <td colspan="4" class="text-center text-muted">No transactions found</td>
                      </tr>
                      <tr v-if="isLoading">
                        <td colspan="4" class="text-center text-muted">Loading...</td>
                      </tr>
                      <tr v-for="transaction in recentTransactions" :key="transaction.id" class="clickable-row" @click="viewStatement" style="cursor: pointer;">
                        <td>{{ formatDate(transaction.date) }}</td>
                        <td>{{ transaction.description }}</td>
                        <td>
                          <label v-if="transaction.type === 'DEBIT' && transaction.referenceType === 'INVOICE'" class="badge badge-warning">Invoice</label>
                          <label v-else-if="transaction.type === 'CREDIT' && transaction.referenceType === 'PAYMENT'" class="badge badge-success">Payment</label>
                          <label v-else-if="transaction.type === 'CREDIT' && transaction.referenceType === 'CREDIT_NOTE'" class="badge badge-info">Credit Note</label>
                          <label v-else-if="transaction.type === 'CREDIT' && transaction.referenceType === 'REFUND'" class="badge badge-primary">Refund</label>
                          <label v-else class="badge badge-secondary">{{ enumToHumanReadable(transaction.referenceType || transaction.type) }}</label>
                        </td>
                        <td class="text-right">
                          <span v-if="transaction.type === 'DEBIT'" style="color: orangered; font-weight: 500;">
                            - {{ displayAmount(currency, transaction.amount) }}
                          </span>
                          <span v-else style="color: green; font-weight: 500;">
                            + {{ displayAmount(currency, transaction.amount) }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- /Main Wrapper -->
</template>

<script>
import moment from "moment";
import {allowedModules} from "../../utils/modules";
import receiptService from "../../service/receipt.service";
import customerStatementService from "../../service/customer.statement.service";
import {enumToHumanReadable, utcDateToLocal, toLocalDate} from "../../utils/utils";
import {displayAmount, formatDisplayAmount} from "../../utils/money";

export default {
  name: "customer-dashboard",
  title: process.env.SERVER_URI,
  components: {},
  data() {
    return {
      customerName: '',
      customerContext: null,
      recentTransactions: [],
      accountBalance: 0,
      totalPayments: 0,
      totalInvoices: 0,
      recentTransactionsCount: 0,
      currency: 'USD',
      isLoading: false,
    };
  },
  methods: {
    loadCustomerContext() {
      const contextData = localStorage.getItem('selectedContext');
      if (contextData) {
        this.customerContext = JSON.parse(contextData);
        this.customerName = this.customerContext.displayName || '';
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return toLocalDate(date);
    },
    displayAmount(currency, amount) {
      return displayAmount(currency, amount);
    },
    enumToHumanReadable(value) {
      return enumToHumanReadable(value);
    },
    redirectToPayment() {
      const locale = this.$i18n.locale;
      if (locale === 'levies') {
        window.location.href = 'https://pay.estatekeeper.io/levies?returnUrl=https://www.estatekeeper.io';
      } else {
        window.location.href = 'https://go.modial.co/';
      }
    },
    viewStatement() {
      this.$router.push('/statement');
    },
    async fetchStatementData() {
      this.isLoading = true;

      try {
        // Get statement from year 2000 to present
        const startDate = moment('2000-01-01').startOf('day').toDate();
        const endDate = moment().endOf('day').toDate();
        
        const params = {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          templateId: 'customer-statement-1'
        };

        const response = await customerStatementService.find(localStorage.getItem("mid"), params);
        
        if (response.data.code !== 'SUCCESS') {
          console.error('Failed to retrieve statement:', response.data.description);
          return;
        }

        const statement = response.data.data;
        
        // Update metrics
        this.accountBalance = statement.closingBalance || 0;
        this.totalPayments = statement.totalCredits || 0;
        this.totalInvoices = statement.totalDebits || 0;
        this.currency = statement.currency || 'USD';
        
        // Get recent transactions (last 10, sorted newest first)
        const transactions = (statement.statementLines || []).map(line => ({
          ...line,
          amount: line.amount,
          balanceAfter: line.balanceAfter,
        }));
        
        this.recentTransactions = transactions
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 10);
        this.recentTransactionsCount = transactions.length;
        
      } catch (e) {
        console.error('Error fetching statement:', e);
        if (e.response && (e.response.status === 401 || e.response.status === 403 || e.response.status === 409)) {
          return;
        }
        // Silent fail - show empty data
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.loadCustomerContext();
    this.fetchStatementData();
  }
};
</script>

<style scoped>
.dash-widget-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
}

.card {
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}
</style>



// WEBPACK FOOTER //
// src/components/dashboards/DefaultDashboard.vue